// El menú "⋯" de la cabecera del Chat y sus hojas: Nuevo chat · Historial · Memoria ·
// Consumo — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Son las cuatro opciones del menú del asistente, con las MISMAS listas
// (`PanelesChat`): el historial es la misma tabla `agent_chats`, la memoria la misma
// `agent_memories`. Lo montan el panel lateral y la pantalla dedicada, así que el estado
// de las hojas y las cargas viven acá y no en cada cabecera.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Brain,
  Check,
  Coins,
  History,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  deleteChats,
  fijarVisibilidad,
  leerVisibilidad,
  listChats,
  type AgentChatSummary,
} from '../../pages/v2/agentChatApi';
import { ModalConfirmar } from '../../components/ModalConfirmar';
import {
  autorParaElHistorial,
  ETIQUETAS_VISIBILIDAD,
  puedeCambiarVisibilidad,
  VISIBILIDADES,
  type VisibilidadDeChat,
} from '../../pages/v2/chatCompartido';
import { useNombresDelEquipo } from '../../hooks/useNombresDelEquipo';
import {
  deleteMemory,
  fetchMemories,
  type AgentMemory,
} from '../../pages/v2/agentMemoryApi';
import {
  borrarSkillDeEmpresa,
  fetchSkillsDeEmpresa,
} from '../../pages/v2/skills/agentSkillsApi';
import type { SkillDelAgente } from '../../pages/v2/skills/skill';
import { SKILLS_DE_FABRICA } from '../../pages/v2/skills/skillsDeFabrica';
import { ListaSkills } from './ListaSkills';
import {
  ListaHistorialChats,
  ListaMemorias,
  ResumenConsumo,
} from '../../pages/v2/modes/asistente/PanelesChat';
import {
  ACCIONES_SUELTAS,
  ETIQUETAS_CONFIRMACION,
  NIVELES_CONFIRMACION,
  type NivelConfirmacion,
  type PermisosChat,
} from '../confirmaciones';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import { BOTON_ESQUINA, BOTON_FLOTANTE_MOBILE, BOTON_REDONDO_BLANCO, BOTON_REDONDO_GRIS } from './estilosAgente';
import { HojaLateral } from './HojaLateral';

type Hoja = null | 'historial' | 'memoria' | 'skills' | 'consumo' | 'permisos' | 'visibilidad';

const Item: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void; peligro?: boolean }> = ({
  icon,
  label,
  onClick,
  peligro = false,
}) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[14px] transition-colors hover:bg-neutral-50 active:bg-neutral-100 ${
      peligro ? 'text-[#C33B36]' : 'text-brand-black/90'
    }`}
  >
    <span
      className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
        peligro ? 'bg-[#C33B361A] text-[#C33B36]' : 'bg-[#1644EB1A] text-brand-primary'
      }`}
    >
      {icon}
    </span>
    {label}
  </button>
);

const CAJA = 'rounded-2xl border p-3 text-left transition-colors';
const CAJA_ACTIVA = 'border-[#1644EB]/[0.35] bg-[#1644EB]/[0.06]';
const CAJA_INACTIVA = 'border-[#02062B]/[0.08] hover:bg-neutral-50';

const Marca: React.FC<{ activo: boolean; redondo?: boolean }> = ({ activo, redondo = true }) => (
  <span
    aria-hidden="true"
    className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${
      redondo ? 'rounded-full' : 'rounded-md'
    } ${
      activo
        ? 'border-brand-primary bg-brand-primary text-white'
        : 'border-[#02062B]/[0.2] text-transparent'
    }`}
  >
    <Check className="h-3 w-3" strokeWidth={3} />
  </span>
);

/** Quién ve el chat: dos opciones excluyentes, con la misma caja que los niveles de
 *  generación (ADR-2026-10-13-chats-compartidos-con-la-empresa). */
const ListaVisibilidad: React.FC<{
  valor: VisibilidadDeChat;
  guardando: boolean;
  onElegir: (v: VisibilidadDeChat) => void;
}> = ({ valor, guardando, onElegir }) => (
  <div role="radiogroup" aria-label="Quién ve este chat" aria-busy={guardando} className="space-y-2">
    {VISIBILIDADES.map((v) => {
      const activo = valor === v;
      const { titulo, detalle } = ETIQUETAS_VISIBILIDAD[v];
      return (
        <button
          key={v}
          type="button"
          role="radio"
          aria-checked={activo}
          disabled={guardando}
          onClick={() => onElegir(v)}
          className={`flex w-full items-start gap-3 ${CAJA} ${activo ? CAJA_ACTIVA : CAJA_INACTIVA} disabled:opacity-60`}
        >
          <Marca activo={activo} />
          <span className="min-w-0">
            <span className="block text-[14px] font-medium text-brand-black">{titulo}</span>
            <span className="mt-0.5 block text-[12.5px] leading-snug text-[#02062B]/[0.55]">
              {detalle}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

/** El nivel de GENERACIÓN: tres opciones excluyentes, en orden de menos a más autonomía.
 *  Radios y no toggles — el del medio no se entiende como "medio prendido". */
const NivelGeneracion: React.FC<{
  valor: NivelConfirmacion;
  onElegir: (n: NivelConfirmacion) => void;
}> = ({ valor, onElegir }) => (
  <div role="radiogroup" aria-label="Cuándo preguntar antes de generar" className="space-y-2">
    {NIVELES_CONFIRMACION.map((nivel) => {
      const activo = valor === nivel;
      const { titulo, detalle } = ETIQUETAS_CONFIRMACION[nivel];
      return (
        <button
          key={nivel}
          type="button"
          role="radio"
          aria-checked={activo}
          onClick={() => onElegir(nivel)}
          className={`flex w-full items-start gap-3 ${CAJA} ${activo ? CAJA_ACTIVA : CAJA_INACTIVA}`}
        >
          <Marca activo={activo} />
          <span className="min-w-0">
            <span className="block text-[14px] font-medium text-brand-black">{titulo}</span>
            <span className="mt-0.5 block text-[12.5px] leading-snug text-[#02062B]/[0.55]">
              {detalle}
            </span>
          </span>
        </button>
      );
    })}
  </div>
);

/** Las acciones que NO son generación: preguntar o directo. Un switch cada una — son
 *  independientes, y quien deja el catálogo suelto no necesariamente quiere lo mismo con
 *  los scrapers. La advertencia se muestra sólo cuando está en directo: leerla antes de
 *  tocar es ruido; leerla después es lo que hace falta. */
const AccionSuelta: React.FC<{
  titulo: string;
  detalle: string;
  advertencia: string;
  directo: boolean;
  onCambiar: (directo: boolean) => void;
}> = ({ titulo, detalle, advertencia, directo, onCambiar }) => (
  <button
    type="button"
    role="switch"
    aria-checked={directo}
    onClick={() => onCambiar(!directo)}
    className={`flex w-full items-start gap-3 ${CAJA} ${directo ? CAJA_ACTIVA : CAJA_INACTIVA}`}
  >
    <Marca activo={directo} redondo={false} />
    <span className="min-w-0">
      <span className="block text-[14px] font-medium text-brand-black">{titulo}</span>
      <span className="mt-0.5 block text-[12.5px] leading-snug text-[#02062B]/[0.55]">
        {directo ? `Sin preguntar. ${advertencia}` : `Te pregunta antes. ${detalle}`}
      </span>
    </span>
  </button>
);

const Titulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[12.5px] font-medium uppercase tracking-wide text-[#3C3C43]/55">
    {children}
  </p>
);

const ListaPermisos: React.FC<{
  permisos: PermisosChat;
  onCambiar: <K extends keyof PermisosChat>(clave: K, valor: PermisosChat[K]) => void;
}> = ({ permisos, onCambiar }) => (
  <div className="space-y-5">
    <p className="text-[13px] leading-snug text-[#02062B]/[0.6]">
      Todo lo que Atlas hace por su cuenta —generar, sumar productos, investigar— te
      pregunta antes. Acá elegís de qué se puede encargar solo.
    </p>

    <div className="space-y-2">
      <Titulo>Generar imágenes y videos</Titulo>
      <NivelGeneracion
        valor={permisos.generacion}
        onElegir={(n) => onCambiar('generacion', n)}
      />
    </div>

    <div className="space-y-2">
      <Titulo>Otras acciones</Titulo>
      {ACCIONES_SUELTAS.map((a) => (
        <AccionSuelta
          key={a.clave}
          titulo={a.titulo}
          detalle={a.detalle}
          advertencia={a.advertencia}
          directo={permisos[a.clave] === 'directo'}
          onCambiar={(directo) => onCambiar(a.clave, directo ? 'directo' : 'preguntar')}
        />
      ))}
    </div>
  </div>
);

export const MenuChat: React.FC<{
  /** "Historial de chats" al lado del "⋯", en pantallas anchas. `true` es el pill de la
   *  esquina derecha del encabezado de la pantalla dedicada
   *  (ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2); `'icono'` es el mismo
   *  botón reducido a su ícono, para cuando el lienzo deja la columna angosta y el pill
   *  no entra (ADR-2026-10-08-historial-como-icono-con-el-lienzo). El panel lateral, más
   *  angosto todavía, lo deja adentro del menú. */
  mostrarHistorial?: boolean | 'icono';
  /** `gris`: el "⋯" con el mismo relleno que los pills de esquina. `fantasma`: sin
   *  relleno, para la cabecera del panel, donde comparte fila con expandir y cerrar.
   *  `flotante`: el look del botón de volver del cascarón mobile (48px, blanco, sombra),
   *  para cuando el menú vive en esa banda
   *  (ADR-2026-09-12-el-agente-en-mobile-usa-la-banda-del-volver). `blanco`: el de las
   *  esquinas de Crear (blanco con sombra), sobre el fondo azul del Asistente
   *  (ADR-2026-10-16-el-agente-se-ve-como-el-asistente). */
  estilo?: 'fantasma' | 'gris' | 'flotante' | 'blanco';
  /** Sin Nuevo chat ni Historial: la pantalla puso los suyos a la vista
   *  (ADR-2026-10-13-nuevo-chat-e-historial-a-la-vista-en-el-asistente). */
  sinNuevoNiHistorial?: boolean;
}> = ({ mostrarHistorial = false, estilo = 'fantasma', sinNuevoNiHistorial = false }) => {
  const chat = useChatAtlas();
  const { companyId, usuarioId } = useEntornoChatAtlas();
  // Para "de Juli" en el historial y para ofrecer "Visibilidad" sólo con equipo.
  const equipo = useNombresDelEquipo(companyId);
  const [abierto, setAbierto] = useState(false);
  const [hoja, setHoja] = useState<Hoja>(null);
  const raizRef = useRef<HTMLDivElement | null>(null);

  const [chats, setChats] = useState<AgentChatSummary[]>([]);
  const [cargandoChats, setCargandoChats] = useState(false);
  const [memorias, setMemorias] = useState<AgentMemory[]>([]);
  const [cargandoMemorias, setCargandoMemorias] = useState(false);
  const [skills, setSkills] = useState<SkillDelAgente[]>([]);
  const [cargandoSkills, setCargandoSkills] = useState(false);
  const [consumo, setConsumo] = useState<number | null>(null);
  const [cargandoConsumo, setCargandoConsumo] = useState(false);
  /** De quién es el chat actual y cómo está. Se lee al abrir el menú, sólo con equipo:
   *  la opción "Visibilidad" la ve el dueño (ADR-2026-10-13-chats-compartidos-con-la-empresa). */
  const [visibilidad, setVisibilidad] = useState<{
    chatId: string;
    duenoId: string | null;
    valor: VisibilidadDeChat;
  } | null>(null);
  const [guardandoVisibilidad, setGuardandoVisibilidad] = useState(false);
  const getChatId = chat.motor.getChatId;
  useEffect(() => {
    if (!abierto || !equipo.varios) return;
    // Lo leído para el chat anterior se tira ANTES de leer: si no, con una lectura lenta la
    // opción se ofrecía con el estado viejo y cambiaba el chat anterior (review atlas-code#176).
    setVisibilidad(null);
    const id = getChatId();
    if (!id) return;
    let activo = true;
    void leerVisibilidad(id).then((r) => {
      if (!activo) return;
      setVisibilidad(r ? { chatId: id, duenoId: r.duenoId, valor: r.visibilidad } : null);
    });
    return () => {
      activo = false;
    };
  }, [abierto, equipo.varios, getChatId]);
  const ofreceVisibilidad = puedeCambiarVisibilidad({
    varios: equipo.varios,
    chatId: visibilidad?.chatId ?? null,
    usuarioId,
    duenoId: visibilidad?.duenoId,
  });
  const elegirVisibilidad = useCallback(
    async (v: VisibilidadDeChat) => {
      if (!visibilidad || visibilidad.valor === v) return;
      // Sólo sobre el chat que está en pantalla: si cambió desde que se leyó, no se toca.
      if (getChatId() !== visibilidad.chatId) {
        setVisibilidad(null);
        setHoja(null);
        return;
      }
      setGuardandoVisibilidad(true);
      const ok = await fijarVisibilidad(visibilidad.chatId, v);
      if (ok) setVisibilidad({ ...visibilidad, valor: v });
      setGuardandoVisibilidad(false);
    },
    [visibilidad, getChatId],
  );

  useEffect(() => {
    if (!abierto) return;
    const alClick = (ev: MouseEvent | TouchEvent) => {
      if (!raizRef.current?.contains(ev.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alClick);
    document.addEventListener('touchstart', alClick);
    return () => {
      document.removeEventListener('mousedown', alClick);
      document.removeEventListener('touchstart', alClick);
    };
  }, [abierto]);

  // ── Borrar el chat que está en pantalla (ADR-2026-10-18-borrar-chat-desde-el-menu). Lo ve
  //    quien puede borrarlo: con equipo, sólo el dueño (`deleteChats` filtra igual por
  //    usuario); sin equipo, los chats de la empresa son todos suyos. Con confirmación, y
  //    esperando a la base: es definitivo y no hay papelera.
  const navigate = useNavigate();
  const location = useLocation();
  const chatIdActual = getChatId();
  const puedeBorrar =
    Boolean(chatIdActual) && (!equipo.varios || (visibilidad?.chatId === chatIdActual && visibilidad.duenoId === usuarioId));
  const [aBorrar, setABorrar] = useState<string | null>(null);
  const [borrando, setBorrando] = useState(false);
  const [falloAlBorrar, setFalloAlBorrar] = useState(false);
  const borrarChat = useCallback(async () => {
    if (!aBorrar || !usuarioId) return;
    setBorrando(true);
    const borrados = await deleteChats([aBorrar], usuarioId);
    setBorrando(false);
    if (!borrados.includes(aBorrar)) {
      setFalloAlBorrar(true);
      return;
    }
    setABorrar(null);
    // El de la pantalla ya no existe: se arranca uno nuevo, como "Nuevo chat". Si la ruta
    // lo nombra (una sesión de Crear `/redes/:id`, de Catálogo), se vuelve a la base de esa
    // pantalla; si no, seguiría guardando sobre una fila borrada.
    chat.reiniciar();
    const sufijo = `/${aBorrar}`;
    if (location.pathname.endsWith(sufijo)) {
      navigate(location.pathname.slice(0, -sufijo.length) || '/', { replace: true });
    }
  }, [aBorrar, usuarioId, chat, location.pathname, navigate]);

  const abrirHistorial = useCallback(async () => {
    setAbierto(false);
    setHoja('historial');
    setCargandoChats(true);
    setChats(await listChats());
    setCargandoChats(false);
  }, []);

  const abrirMemoria = useCallback(async () => {
    setAbierto(false);
    setHoja('memoria');
    if (!companyId) {
      setMemorias([]);
      return;
    }
    setCargandoMemorias(true);
    setMemorias(await fetchMemories(companyId));
    setCargandoMemorias(false);
  }, [companyId]);

  const fetchConsumo = chat.fetchConsumo;
  const abrirConsumo = useCallback(async () => {
    setAbierto(false);
    setHoja('consumo');
    setCargandoConsumo(true);
    setConsumo(await fetchConsumo());
    setCargandoConsumo(false);
  }, [fetchConsumo]);

  const borrarMemoria = async (id: string) => {
    setMemorias((prev) => prev.filter((m) => m.id !== id));
    await deleteMemory(id);
  };

  // Skills (ADR-2026-09-09-el-agente-tiene-skills): las de la empresa se leen de la
  // tabla; las de fábrica vienen del repo y no cargan nada.
  const abrirSkills = useCallback(async () => {
    setAbierto(false);
    setHoja('skills');
    if (!companyId) {
      setSkills([]);
      return;
    }
    setCargandoSkills(true);
    setSkills(await fetchSkillsDeEmpresa(companyId));
    setCargandoSkills(false);
  }, [companyId]);

  const borrarSkill = async (id: string) => {
    setSkills((prev) => prev.filter((s) => s.id !== id));
    await borrarSkillDeEmpresa(id);
  };

  const cerrarHoja = useCallback(() => setHoja(null), []);

  return (
    <div ref={raizRef} className="relative flex shrink-0 items-center gap-2">
      {mostrarHistorial === 'icono' ? (
        <button
          type="button"
          onClick={abrirHistorial}
          aria-label="Historial de chats"
          title="Historial de chats"
          className={`hidden md:inline-flex ${BOTON_REDONDO_GRIS}`}
        >
          <History className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
        </button>
      ) : (
        mostrarHistorial && (
          <button type="button" onClick={abrirHistorial} className={`hidden md:inline-flex ${BOTON_ESQUINA}`}>
            <History className="h-4 w-4" strokeWidth={2.25} aria-hidden="true" />
            Historial de chats
          </button>
        )
      )}
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label="Opciones del chat"
        aria-haspopup="menu"
        aria-expanded={abierto}
        className={
          estilo === 'gris'
            ? `${BOTON_REDONDO_GRIS} ${abierto ? 'bg-neutral-200' : ''}`
            : estilo === 'flotante'
              ? `${BOTON_FLOTANTE_MOBILE} w-12 ${abierto ? 'opacity-70' : ''}`
              : estilo === 'blanco'
                ? `${BOTON_REDONDO_BLANCO} ${abierto ? 'bg-[#F3F4F8]' : ''}`
              : `inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                  abierto
                    ? 'bg-[#02062B]/[0.09] text-brand-black'
                    : 'text-[#02062B]/[0.6] hover:bg-[#02062B]/[0.06] hover:text-brand-black'
                }`
        }
      >
        <MoreHorizontal className={estilo === 'gris' ? 'h-4 w-4' : 'h-5 w-5'} strokeWidth={2.25} aria-hidden="true" />
      </button>

      {abierto && (
        <div
          role="menu"
          className="absolute right-0 top-full z-[56] mt-2 w-[240px] rounded-2xl border border-[#02062B]/[0.06] bg-white p-1.5 shadow-xl"
        >
          {!sinNuevoNiHistorial && (
            <>
              <Item
                icon={<Plus className="h-4 w-4" />}
                label="Nuevo chat"
                onClick={() => {
                  setAbierto(false);
                  chat.reiniciar();
                }}
              />
              <Item icon={<History className="h-4 w-4" />} label="Historial" onClick={abrirHistorial} />
            </>
          )}
          <Item icon={<Brain className="h-4 w-4" />} label="Memoria" onClick={abrirMemoria} />
          <Item icon={<Sparkles className="h-4 w-4" />} label="Skills" onClick={abrirSkills} />
          <Item icon={<Coins className="h-4 w-4" />} label="Consumo" onClick={abrirConsumo} />
          <Item
            icon={<ShieldCheck className="h-4 w-4" />}
            label="Permisos"
            onClick={() => {
              setAbierto(false);
              setHoja('permisos');
            }}
          />
          {ofreceVisibilidad && (
            <Item
              icon={<Users className="h-4 w-4" />}
              label="Visibilidad"
              onClick={() => {
                setAbierto(false);
                setHoja('visibilidad');
              }}
            />
          )}
          {puedeBorrar && chatIdActual && (
            <Item
              icon={<Trash2 className="h-4 w-4" />}
              label="Borrar chat"
              peligro
              onClick={() => {
                setAbierto(false);
                setFalloAlBorrar(false);
                setABorrar(chatIdActual);
              }}
            />
          )}
        </div>
      )}

      <ModalConfirmar
        abierto={aBorrar !== null}
        titulo="Borrar este chat"
        detalle={
          falloAlBorrar
            ? 'No se pudo borrar. Probá de nuevo en un momento.'
            : 'Se borra la conversación entera. No se puede deshacer. Lo que generaste sigue en tus creaciones.'
        }
        confirmar="Borrar chat"
        trabajando={borrando}
        onCancelar={() => setABorrar(null)}
        onConfirmar={borrarChat}
      />

      <HojaLateral abierta={hoja === 'historial'} titulo="Historial de chats" onCerrar={cerrarHoja}>
        <ListaHistorialChats
          chats={chats}
          cargando={cargandoChats}
          onElegir={(id) => {
            setHoja(null);
            void chat.cargarChat(id);
          }}
          autorDe={(c) =>
            autorParaElHistorial({ duenoId: c.user_id, usuarioId, nombreDe: equipo.nombreDe })
          }
        />
      </HojaLateral>
      <HojaLateral
        abierta={hoja === 'memoria'}
        titulo="Memoria de Atlas"
        bajada="Lo que Atlas recuerda de vos y tu marca entre chats."
        onCerrar={cerrarHoja}
      >
        <ListaMemorias memorias={memorias} cargando={cargandoMemorias} onBorrar={borrarMemoria} />
      </HojaLateral>
      <HojaLateral
        abierta={hoja === 'skills'}
        titulo="Skills de Atlas"
        bajada="Cómo sabe hacer cada cosa, y lo que vos le enseñaste."
        onCerrar={cerrarHoja}
      >
        <ListaSkills
          deEmpresa={skills}
          deFabrica={SKILLS_DE_FABRICA}
          cargando={cargandoSkills}
          onBorrar={borrarSkill}
        />
      </HojaLateral>
      <HojaLateral abierta={hoja === 'consumo'} titulo="Consumo" onCerrar={cerrarHoja}>
        <ResumenConsumo creditos={consumo} cargando={cargandoConsumo} />
      </HojaLateral>
      <HojaLateral
        abierta={hoja === 'permisos'}
        titulo="Permisos"
        bajada="De qué se puede encargar Atlas sin preguntarte."
        onCerrar={cerrarHoja}
      >
        <ListaPermisos permisos={chat.permisos} onCambiar={chat.setPermiso} />
      </HojaLateral>
      <HojaLateral
        abierta={hoja === 'visibilidad'}
        titulo="Visibilidad del chat"
        bajada="Quién ve este chat y puede seguir la conversación."
        onCerrar={cerrarHoja}
      >
        {visibilidad && (
          <ListaVisibilidad
            valor={visibilidad.valor}
            guardando={guardandoVisibilidad}
            onElegir={(v) => void elegirVisibilidad(v)}
          />
        )}
      </HojaLateral>
    </div>
  );
};

export default MenuChat;
